import type { ITheme } from "@xterm/xterm";
import { PANEL_THEME, themeById } from "./themes.js";

/*
 * The terminal's colours. «Как в панели» (the default): background, text,
 * cursor and selection from our tokens (`--term-*` in styles.css, light and
 * dark), the 16 ANSI colours picked to read on each background. Any other
 * theme comes from `themes.ts`.
 */

const LIGHT_ANSI = {
  black: "#1e293b",
  red: "#b91c1c",
  green: "#15803d",
  yellow: "#a16207",
  blue: "#1d4ed8",
  magenta: "#a21caf",
  cyan: "#0e7490",
  white: "#64748b",
  brightBlack: "#475569",
  brightRed: "#dc2626",
  brightGreen: "#16a34a",
  brightYellow: "#ca8a04",
  brightBlue: "#2563eb",
  brightMagenta: "#c026d3",
  brightCyan: "#0891b2",
  brightWhite: "#334155",
};

const DARK_ANSI = {
  black: "#1e293b",
  red: "#f87171",
  green: "#4ade80",
  yellow: "#facc15",
  blue: "#60a5fa",
  magenta: "#e879f9",
  cyan: "#22d3ee",
  white: "#cbd5e1",
  brightBlack: "#64748b",
  brightRed: "#fca5a5",
  brightGreen: "#86efac",
  brightYellow: "#fde047",
  brightBlue: "#93c5fd",
  brightMagenta: "#f0abfc",
  brightCyan: "#67e8f9",
  brightWhite: "#f8fafc",
};

/** The xterm theme of a chosen theme id; the panel's own look for `panel` or an unknown id. */
export function terminalTheme(themeId: string = PANEL_THEME, root?: HTMLElement): ITheme {
  const spec = themeId === PANEL_THEME ? undefined : themeById(themeId);
  if (!spec) return panelTheme(root ?? document.documentElement);
  const { background, foreground, cursor, selection, ...ansi } = spec.colors;
  return {
    background,
    foreground,
    cursor,
    cursorAccent: background,
    selectionBackground: selection,
    ...ansi,
  };
}

/** The theme for the page's current light or dark look. */
function panelTheme(root: HTMLElement): ITheme {
  const css = getComputedStyle(root);
  const token = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  const dark = css.colorScheme.includes("dark");
  return {
    background: token("--term-bg", dark ? "#0a1020" : "#fbfcfe"),
    foreground: token("--term-fg", dark ? "#dbe3ee" : "#1e293b"),
    cursor: token("--term-cursor", "#0f9488"),
    cursorAccent: token("--term-bg", "#ffffff"),
    selectionBackground: token("--term-selection", "rgba(15, 148, 136, 0.22)"),
    ...(dark ? DARK_ANSI : LIGHT_ANSI),
  };
}

/** Calls `onChange` when the page switches between light and dark; returns the unsubscribe. */
export function onThemeChange(onChange: () => void): () => void {
  const media = matchMedia("(prefers-color-scheme: dark)");
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
  media.addEventListener("change", onChange);
  return () => {
    observer.disconnect();
    media.removeEventListener("change", onChange);
  };
}
